import { useQueries } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent } from 'react';
import type { audience } from '@bb/contracts';
import { Button } from '@bb/ui';
import { ApiError, type AdminApi } from '../../api/client.ts';
import { AudienceBuilder, emptyAudience } from '../audience/AudienceBuilder.tsx';
import { formatCount } from '../audience/ChannelReachBreakdown.tsx';

type Definition = audience.AudienceDefinition;
type Node = audience.FilterNode;

export interface AudiencePathsGroup {
  id: string;
  name: string;
  audience: Definition;
  exitAfter?: boolean;
}

/** Config of an Audience Paths step (BB-118), as the `audience_paths` handler reads it. */
export interface AudiencePathsValue {
  groups: AudiencePathsGroup[];
  everyoneElse: { exitAfter?: boolean };
}

export const MAX_GROUPS = 8;
const EVERYONE_ELSE = 'Everyone Else';

const prefixIds = (node: Node, prefix: string): Node =>
  node.kind === 'filter' ? { ...node, id: `${prefix}${node.id}` } : node.kind === 'not' ? { ...node, id: `${prefix}${node.id}`, child: prefixIds(node.child, prefix) } : { ...node, id: `${prefix}${node.id}`, children: node.children.map((c) => prefixIds(c, prefix)) };

/** Profiles in both audiences (node ids kept unique), for the overlap warning. */
export function intersect(a: Definition, b: Definition): Definition {
  return {
    schemaVersion: 1,
    include: { kind: 'and', id: 'overlap', children: [prefixIds(a.include, 'a_'), prefixIds(b.include, 'b_')] },
    exclude: [...a.exclude.map((x) => prefixIds(x, 'a_')), ...b.exclude.map((x) => prefixIds(x, 'b_'))],
    apps: a.apps.mode === 'all' ? b.apps : a.apps,
  };
}

const nextId = (groups: AudiencePathsGroup[]) => {
  for (let n = groups.length + 1; ; n++) if (!groups.some((g) => g.id === `group-${n}`)) return `group-${n}`;
};

/** Which group a user goes to: the first whose audience they match (User Lookup per group, BB-45). */
function WhichGroup({ environmentId, value }: { environmentId: string; value: AudiencePathsValue }) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const [externalId, setExternalId] = useState('');
  const [result, setResult] = useState<{ group: string; reasons: Array<{ group: string; why: string }> } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const check = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    const reasons: Array<{ group: string; why: string }> = [];
    try {
      for (const group of value.groups) {
        const answer = await api.lookupAudience(environmentId, group.audience, { by: 'external_id', value: externalId.trim() }, 'en');
        if (answer.match) return setResult({ group: group.name, reasons });
        reasons.push({ group: group.name, why: answer.blockers.map((b) => b.sentence).join('; ') || 'no match' });
      }
      setResult({ group: EVERYONE_ELSE, reasons });
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'profile_not_found' ? 'No such user in this environment.' : 'The user could not be checked.');
    }
  };
  return (
    <form aria-label="Which group does a user get" onSubmit={(e) => void check(e)} className="filters">
      <input aria-label="External ID to check" value={externalId} onChange={(e) => setExternalId(e.target.value)} placeholder="External ID" />
      <Button type="submit" size="sm" disabled={!externalId.trim()}>
        Which group?
      </Button>
      {error && <span role="alert">{error}</span>}
      {result && (
        <div role="status">
          Goes to «{result.group}».
          {result.reasons.length > 0 && (
            <ul>
              {result.reasons.map((r) => (
                <li key={r.group}>
                  Not «{r.group}»: {r.why}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </form>
  );
}

/** Pairs of groups that share users: the earlier group takes them (estimates, BB-44). */
function OverlapWarnings({ environmentId, groups }: { environmentId: string; groups: AudiencePathsGroup[] }) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const [settled, setSettled] = useState(groups);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(groups), 800);
    return () => clearTimeout(timer);
  }, [groups]);
  const pairs = settled.flatMap((a, i) => settled.slice(i + 1).map((b) => ({ a, b })));
  const results = useQueries({
    queries: pairs.map(({ a, b }) => ({ queryKey: ['audience-estimate', environmentId, intersect(a.audience, b.audience)], queryFn: () => api.estimateAudience(environmentId, intersect(a.audience, b.audience)), retry: false })),
  });
  const overlaps = pairs.map((p, i) => ({ ...p, count: results[i]?.data?.count ?? 0 })).filter((o) => o.count > 0);
  if (overlaps.length === 0) return null;
  return (
    <ul aria-label="Overlapping groups" className="notice notice--warning">
      {overlaps.map(({ a, b, count }) => (
        <li key={`${a.id}:${b.id}`}>
          ≈{formatCount(count)} users match both «{a.name}» and «{b.name}»; they go to «{a.name}», the earlier group.
        </li>
      ))}
    </ul>
  );
}

/**
 * Editor of an Audience Paths step (BB-118): groups in order, each with its audience; the first group a user matches
 * takes them; «Everyone Else» is always last. A running Canvas keeps the order and the set of groups (`locked`).
 * Shows which group a given user would get and where groups overlap.
 */
export function AudiencePathsPanel({ environmentId, value, onChange, locked = false, readOnly = false }: { environmentId: string; value: AudiencePathsValue; onChange: (next: AudiencePathsValue) => void; locked?: boolean; readOnly?: boolean }) {
  const setGroup = (index: number, patch: Partial<AudiencePathsGroup>) => onChange({ ...value, groups: value.groups.map((g, i) => (i === index ? { ...g, ...patch } : g)) });
  const move = (index: number, delta: number) => {
    const groups = [...value.groups];
    const [g] = groups.splice(index, 1);
    groups.splice(index + delta, 0, g!);
    onChange({ ...value, groups });
  };
  const structural = !readOnly && !locked;
  return (
    <section aria-label="Audience Paths" className="audience-paths">
      <p className="hint">Users go to the first group whose audience they match, checked in this order when they reach the step.</p>
      {locked && <p className="hint">The Canvas is running: the order and the set of groups cannot change.</p>}
      <ol className="audience-paths__groups">
        {value.groups.map((group, index) => (
          <li key={group.id}>
            <section aria-label={`Group ${index + 1}: ${group.name}`}>
              <div className="filters">
                <input aria-label={`Name of group ${index + 1}`} value={group.name} disabled={readOnly} onChange={(e) => setGroup(index, { name: e.target.value })} />
                <label>
                  <input type="checkbox" checked={Boolean(group.exitAfter)} disabled={!structural} onChange={(e) => setGroup(index, { exitAfter: e.target.checked || undefined })} /> End the Canvas for this group
                </label>
                {structural && (
                  <>
                    <Button size="sm" variant="ghost" aria-label={`Move group ${group.name} up`} disabled={index === 0} onClick={() => move(index, -1)}>
                      ↑
                    </Button>
                    <Button size="sm" variant="ghost" aria-label={`Move group ${group.name} down`} disabled={index === value.groups.length - 1} onClick={() => move(index, 1)}>
                      ↓
                    </Button>
                    <Button size="sm" variant="ghost" disabled={value.groups.length === 1} onClick={() => onChange({ ...value, groups: value.groups.filter((_, i) => i !== index) })}>
                      Remove group {group.name}
                    </Button>
                  </>
                )}
              </div>
              <AudienceBuilder environmentId={environmentId} value={group.audience} readOnly={readOnly} onChange={(audience) => setGroup(index, { audience })} />
            </section>
          </li>
        ))}
      </ol>
      {structural && value.groups.length < MAX_GROUPS && (
        <Button
          size="sm"
          onClick={() => {
            const id = nextId(value.groups);
            onChange({ ...value, groups: [...value.groups, { id, name: `Group ${value.groups.length + 1}`, audience: emptyAudience() }] });
          }}
        >
          Add group
        </Button>
      )}
      <section aria-label={EVERYONE_ELSE} className="audience-paths__else">
        <h4>{EVERYONE_ELSE}</h4>
        <p className="hint">Users no group took. Always the last branch.</p>
        <label>
          <input type="checkbox" checked={Boolean(value.everyoneElse.exitAfter)} disabled={!structural} onChange={(e) => onChange({ ...value, everyoneElse: e.target.checked ? { exitAfter: true } : {} })} /> End the Canvas for everyone else
        </label>
      </section>
      <OverlapWarnings environmentId={environmentId} groups={value.groups} />
      <WhichGroup environmentId={environmentId} value={value} />
    </section>
  );
}
